import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import { CircleDollarSign, ClipboardList, Fuel } from 'lucide-react';
import type { ReactNode } from 'react';

import { CabecalhoPagina } from '@/componentes/CabecalhoPagina';
import { CardIndicador } from '@/componentes/CardIndicador';
import { FaixaIndicadores } from '@/componentes/FaixaIndicadores';
import { HistoriaNaMesa } from '@/historias/mesa';
import { formatarInteiro, formatarReais } from '@/lib/formato';
import { useSessao } from '@/modulos/sessao/consultas';

import { abaAtiva } from './abas';
import { useAbastecimentos, useCobrancas, useOrdens, useTanques } from './consultas';
import { gastoDosAbastecimentos } from './tipos';

/**
 * O combustível como UMA tela com abas (melhoria pedida pelo dono em 25/09).
 *
 * Antes eram oito opções no menu, e quem estava em Tanques voltava ao menu
 * para ver as Ordens. Agora o item do menu abre o hub, e as oito telas viram
 * abas logo abaixo dos indicadores. Cada aba continua sendo um ENDEREÇO
 * próprio (`/combustivel/tanques`, ...): o link que alguém mandou por
 * mensagem continua abrindo a mesma aba, e o botão voltar do navegador anda
 * entre as abas como anda entre as telas.
 *
 * ## O que fica fixo e o que muda com a aba
 *
 * Fixos: o título, a ação "Lançar abastecimento" no canto (a ação que o
 * módulo existe para provocar, e por isso a laranja) e os quatro números que
 * alguém pergunta de manhã — quanto saiu, quanto tem nos tanques, o que está
 * autorizado e o que o posto cobrou diferente. Cada card leva à aba dele.
 *
 * Muda: o texto que explica a aba, e o conteúdo dela.
 *
 * Na aba de lançar, o botão do canto sai: ali o laranja é o do formulário, e
 * a regra é uma ação laranja por tela.
 */
export function HubDoCombustivel({ children }: { readonly children: ReactNode }) {
  const caminho = useRouterState({ select: (estado) => estado.location.pathname });
  const navegar = useNavigate();
  const aba = abaAtiva(caminho);
  /* O laranja do hub leva ao formulário de lançar: só para quem lança (prova
     do marco, 07/10/2026). A Diretoria lê o combustível e não lança. */
  const sessao = useSessao();
  const podeLancar = (sessao.data?.permissoes ?? []).includes('abastecimento.registrar');
  return (
    <div className="min-w-0 flex-1 overflow-auto">
      <div className="pagina">
        <CabecalhoPagina
          sobretitulo="Frota"
          titulo="Combustível"
          subtitulo="Abastecimento, tanques, ordens, consumo e cobranças da frota, numa tela só"
          acoes={
            aba.para === '/combustivel/lancamento' || aba.para === '/combustivel/lancamento/completo' || !podeLancar ? null : (
              <HistoriaNaMesa codigo="BTN-CMB-01" className="flex">
                <Link to="/combustivel/lancamento" search={{}} className="botao botao-primario">
                  Lançar abastecimento
                </Link>
              </HistoriaNaMesa>
            )
          }
        />

        <IndicadoresDoCombustivel
          aoAbrir={(para) => {
            void navegar({ to: para });
          }}
        />

        {/* O TRILHO DE ABAS SAIU (pedido do dono, 25/10/2026: *"Eu não quero
            mais abas internas"*). As nove telas são seções da navegação
            lateral, que é a navegação do sistema — o trilho era uma segunda,
            em outro lugar da tela e com outra regra.
            
            A EXPLICAÇÃO DA TELA ABERTA FICOU. Ela não era parte do trilho:
            é a linha que diz o que esta tela responde, e some junto com o
            trilho só se alguém não olhar para o que ela faz. */}
        <p className="txt-apoio max-w-[80ch] text-pretty text-tinta-secundaria">{aba.explicacao}</p>

        {children}
      </div>
    </div>
  );
}

/**
 * O gasto diz sobre o que foi somado. "Em todos os lançamentos" com oito deles
 * sem preço afirmava um gasto que a soma não tem — o mesmo cuidado do "Custo"
 * da aba de lançamento, que conta os que ficaram de fora.
 */
function contextoDoGasto(gasto: number, semPreco: number, cortada: boolean): string {
  if (semPreco === 0) {
    return `${formatarReais(gasto)} de gasto, ${cortada ? 'nos lançamentos mais recentes' : 'em todos os lançamentos'}`;
  }
  return `${formatarReais(gasto)} de gasto${cortada ? ' nos lançamentos mais recentes' : ''}; fora dele, ${formatarInteiro(semPreco)} ${semPreco === 1 ? 'lançamento' : 'lançamentos'} sem preço`;
}

/**
 * Os quatro números do topo. Cada um lê a MESMA consulta da aba dele — mesma
 * chave, uma ida só ao servidor —, então o card e a aba nunca dizem números
 * diferentes.
 *
 * Carregando, o valor e o contexto viram esqueleto (p.32) e o rótulo fica.
 * Sem acesso, o valor é "—" e o contexto diz por quê: um zero no lugar de "não
 * carregou" é o pior tipo de número errado, porque parece certo.
 */
function IndicadoresDoCombustivel({ aoAbrir }: { readonly aoAbrir: (para: string) => void }) {
  const abastecimentos = useAbastecimentos();
  const tanques = useTanques();
  const ordens = useOrdens();
  const cobrancas = useCobrancas();

  const lancados = abastecimentos.data?.linhas ?? [];
  const litros = lancados.reduce((soma, a) => soma + a.litros, 0);
  const { gasto, semPreco } = gastoDosAbastecimentos(lancados);
  const cortada = (abastecimentos.data?.total ?? 0) > lancados.length;

  const saldo = (tanques.data ?? []).reduce((soma, t) => soma + t.saldoCalculadoL, 0);
  const quantosTanques = (tanques.data ?? []).length;

  const abertas = (ordens.data ?? []).filter((o) => o.situacao === 'aberta');
  const autorizado = abertas.reduce((soma, o) => soma + o.saldoL, 0);

  const divergentes = (cobrancas.data ?? []).filter((c) => c.situacao === 'divergente').length;

  /* Carregando, o texto vai vazio: o card desenha o esqueleto no lugar dele. */
  const semDado = (consulta: { isPending: boolean; isError: boolean }) =>
    consulta.isPending ? '' : consulta.isError ? 'não carregou: abra a aba para ver o motivo' : null;

  return (
    <FaixaIndicadores>
      <CardIndicador
        rotulo="Volume lançado"
        icone={Fuel}
        tom="marca"
        carregando={abastecimentos.isPending}
        valor={abastecimentos.isPending ? '' : semDado(abastecimentos) ? '—' : formatarInteiro(litros)}
        {...(semDado(abastecimentos) ? {} : { unidade: 'L' })}
        contexto={semDado(abastecimentos) ?? contextoDoGasto(gasto, semPreco, cortada)}
        aoAbrir={() => {
          aoAbrir('/combustivel');
        }}
      />
      {/* Tom da marca: volume não é status, e o azul era o de "Disponível"
          (p.09: as cores de status são exclusivas do status; R02, p.31: o bloco
          leva o tom do ASSUNTO). A tolerância saiu do contexto: ela é o
          "Acima da capacidade" da aba de tanques, e a mesma resposta não
          aparece duas vezes na tela (p.17). */}
      <CardIndicador
        rotulo="Volume dos tanques"
        icone={Fuel}
        tom="marca"
        carregando={tanques.isPending}
        valor={tanques.isPending ? '' : semDado(tanques) ? '—' : formatarInteiro(saldo)}
        {...(semDado(tanques) ? {} : { unidade: 'L' })}
        contexto={
          semDado(tanques) ??
          `volume somado em ${formatarInteiro(quantosTanques)} ${quantosTanques === 1 ? 'tanque' : 'tanques'}`
        }
        aoAbrir={() => {
          aoAbrir('/combustivel/tanques');
        }}
      />
      {/* Tom da marca: ordem não é status, e o tom `neutro` pegava emprestada a
          tinta de "Não alocado". A R02 (p.31) desenha assunto sem status no
          laranja, como o "Total da frota" do exemplo. */}
      <CardIndicador
        rotulo="Ordens abertas"
        icone={ClipboardList}
        tom="marca"
        carregando={ordens.isPending}
        valor={ordens.isPending ? '' : semDado(ordens) ? '—' : formatarInteiro(abertas.length)}
        /* Espaço não separável entre o número e a unidade (p.12): no card
           estreito o "L" caía sozinho na linha de baixo. */
        contexto={semDado(ordens) ?? `${formatarInteiro(autorizado)}\u00a0L autorizados e ainda não entregues`}
        aoAbrir={() => {
          aoAbrir('/combustivel/ordens');
        }}
      />
      <CardIndicador
        rotulo="Cobranças divergentes"
        icone={CircleDollarSign}
        tom="parada"
        carregando={cobrancas.isPending}
        alerta={semDado(cobrancas) === null && divergentes > 0}
        valor={cobrancas.isPending ? '' : semDado(cobrancas) ? '—' : formatarInteiro(divergentes)}
        contexto={semDado(cobrancas) ?? 'o posto cobrou diferente do que foi lançado'}
        aoAbrir={() => {
          aoAbrir('/combustivel/cobrancas');
        }}
      />
    </FaixaIndicadores>
  );
}
